import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ActivatedRoute, Router } from '@angular/router';
import { ThemeService } from '../../features/theme/theme.service';
import { ToastrService } from 'ngx-toastr';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  noOnlySpacesValidator,
  noOnlySpecialCharsValidator,
} from '../../core/helpers/validator';

@Component({
  selector: 'app-new-theme',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    CommonModule,
    ContentLoaderComponent,
  ],
  templateUrl: './new-theme.component.html',
  styleUrl: './new-theme.component.scss',
})
export class NewThemeComponent {
  public languages: any[] = [];
  public form: FormGroup;
  public themeTranslations: {
    langId: number;
    themeName: string;
  }[] = [];
  themeId: any;
  selected: any;
  updated: boolean = false;
  isEditing: boolean = false;
  loading: boolean = false;
  isSubmitting = false;
  payload: {
    langId: number;
    themeName: string;
  }[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private themeService: ThemeService,
    private toastr: ToastrService,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          noOnlySpecialCharsValidator(),
          noOnlySpacesValidator(),
        ],
      ],
      langId: [null, Validators.required],
    });
  }

  ngOnInit() {
    this.getLanguages();
    this.route.paramMap.subscribe((params) => {
      this.themeId = params.get('id');
      if (this.themeId) {
        this.loading = true;
        if (this.themeTranslations.length === this.languages.length) {
          this.form.get('name')?.disable();
          this.form.get('langId')?.disable();
        }
        this.getThemeById(this.themeId);
      } else {
        this.form.get('langId')?.patchValue(1);
      }
    });
  }

  getLanguages(): void {
    this.themeService.getLanguages().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getThemeById(id: any) {
    this.themeService.getThemeById(id).subscribe({
      next: (res: any) => {
        this.getLanguages();
        const themeData = (res as any).data;
        themeData.themeTranslation.forEach((translation: any) => {
          this.themeTranslations.push({
            langId: translation.langId,
            themeName: translation.themeName,
          });
        });
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }

  getLanguageName(langId: number): string {
    const lang = this.languages.find((l) => l.id === langId);
    return lang ? lang.name : `Lang ID: ${langId}`;
  }

  get sortedTranslations() {
    return this.themeTranslations
      ?.slice()
      .sort((a, b) => (a.langId === 1 ? -1 : b.langId === 1 ? 1 : 0));
  }

  isLangSelected(langId: number): boolean {
    return this.themeTranslations.some((t) => t.langId === langId);
  }

  addTheme(): void {
    const { name, langId } = this.form.value;
    if (this.form.invalid) {
      this.toastr.warning('Please enter a valid theme name and select a language');
      return;
    }
    const isDuplicate = this.themeTranslations.some((t) => t.langId === langId);
    if (isDuplicate) {
      this.toastr.error('This language is already added');
      return;
    }
    this.themeTranslations.push({ themeName: name, langId });
    this.form.reset();
    if (this.themeTranslations.length === this.languages.length) {
      this.form.get('name')?.disable();
      this.form.get('langId')?.disable();
    }
    if (this.selected?.themeName != this.form.value.name) {
      this.updated = true;
      const index = this.payload.findIndex(
        (payload) => payload.langId === langId
      );
      if (index !== -1) {
        this.payload[index] = { themeName: name, langId };
      } else {
        this.payload.push({ themeName: name, langId });
      }
    }
    this.form.reset();
    this.isEditing = false;
  }

  removeTheme(index: number): void {
    this.themeTranslations.splice(index, 1);
    if (this.themeTranslations.length !== this.languages.length) {
      this.form.get('name')?.enable();
      this.form.get('langId')?.enable();
    }
  }

  editTheme(index: number): void {
    if (this.isEditing) {
      this.addTheme();
    }
    this.isEditing = true;
    this.form.get('name')?.enable();
    this.form.get('langId')?.enable();
    this.selected = this.themeTranslations[index];
    this.themeTranslations.splice(index, 1);
    this.form.patchValue({
      name: this.selected.themeName,
      langId: this.selected.langId,
    });
    this.scrollUp('translation');
  }

  scrollUp(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  onCancel() {
    this.router.navigate(['/themes']);
  }

  saveTheme(): void {
    const createPayload = { translations: this.themeTranslations };
    const updatePayload = {
      translations: this.updated ? this.payload : this.themeTranslations,
    };
    this.isSubmitting = true;
    if (this.themeId) {
      this.themeService.updateTheme(this.themeId, updatePayload).subscribe({
        next: (res) => {
          this.toastr.success('Theme updated successfully');
          this.form.reset();
          this.isSubmitting = false;
          this.router.navigate(['/themes']);
        },
        error: (err) => {
          this.isSubmitting = false;
          this.form.reset();
        },
      });
    } else {
      this.themeService.createTheme(createPayload).subscribe({
        next: (res) => {
          this.toastr.success('Theme created successfully');
          this.isSubmitting = false;
          this.form.reset();
          this.router.navigate(['/themes']);
        },
        error: (err) => {
          this.isSubmitting = false;
          this.form.reset();
        },
      });
    }
  }
}
